import { z } from 'zod'

// ============================================
// CLINIC ENTITY SCHEMA
// ============================================

export const clinicSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Clinic name is required'),
  code: z.string().optional(),
  address: z.string().optional(),
  floor: z.string().optional(),
  notes: z.string().optional(),
  color: z.string().optional(), // Hex color for UI
  active: z.boolean().default(true),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

export type Clinic = z.infer<typeof clinicSchema>

// ============================================
// CLINIC REFERENCE (denormalized in assignments)
// ============================================

export const clinicRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional(),
})

export type ClinicRef = z.infer<typeof clinicRefSchema>

// ============================================
// CREATE CLINIC DTO
// ============================================

export const createClinicSchema = z.object({
  name: z.string().min(1, 'Clinic name is required'),
  code: z.string().optional(),
  address: z.string().optional(),
  floor: z.string().optional(),
  notes: z.string().optional(),
  color: z.string().optional(),
  active: z.boolean().default(true),
})

export type CreateClinic = z.infer<typeof createClinicSchema>

// ============================================
// UPDATE CLINIC DTO
// ============================================

export const updateClinicSchema = createClinicSchema.partial()

export type UpdateClinic = z.infer<typeof updateClinicSchema>

// ============================================
// CLINIC ASSIGNMENT SCHEMA (for weekly assignments)
// ============================================

export const clinicAssignmentSchema = z.object({
  id: z.string().optional(),
  date: z.string().min(1, 'Date is required'), // YYYY-MM-DD
  clinicId: z.string().min(1, 'Clinic ID is required'),
  doctorIds: z.array(z.string()).min(1, 'At least one doctor is required'),
  notes: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
})

export type ClinicAssignment = z.infer<typeof clinicAssignmentSchema>

// ============================================
// CREATE/UPDATE CLINIC ASSIGNMENT DTO
// ============================================

export const upsertClinicAssignmentSchema = z.object({
  doctorIds: z.array(z.string()).min(1, 'At least one doctor is required'),
  notes: z.string().optional(),
})

export type UpsertClinicAssignment = z.infer<typeof upsertClinicAssignmentSchema>

// ============================================
// QUERY CLINIC ASSIGNMENTS DTO
// ============================================

export const queryClinicAssignmentsSchema = z.object({
  from: z.string().min(1, 'From date is required'), // YYYY-MM-DD
  to: z.string().min(1, 'To date is required'), // YYYY-MM-DD
})

export type QueryClinicAssignments = z.infer<typeof queryClinicAssignmentsSchema>
